"use client";

import { useEffect, type CSSProperties } from "react";
import { useTranslations } from "next-intl";
import { ClipboardCheck, File } from "lucide-react";
import { cn } from "@/lib/utils";
import ContractStageCard from "./ContractStageCard";
import TenderStageCard from "./TenderStageCard";
import { deriveStage, tabProgress, tenderCounters, type HeroSlide } from "./heroTimeline";
import { useHeroTimeline } from "./useHeroTimeline";

const slideStyle = (index: HeroSlide, phase: "enter" | "play" | "exit", active: boolean, reduced: boolean): CSSProperties => {
  const motion = active && !reduced && phase !== "play";
  return {
    "--hero-x": index === 0 ? "-32px" : "32px",
    animation: !motion
      ? "none"
      : phase === "exit"
        ? "hero-slide-exit 280ms ease forwards"
        : "hero-slide-enter 380ms ease forwards",
  } as CSSProperties;
};

export default function HeroCarousel() {
  const t = useTranslations("home.slides");
  const { activeSlide, phase, elapsed, cycleId, reduced, selectSlide, selectStep, setPaused } = useHeroTimeline();
  const shown = deriveStage(phase === "enter" ? 0 : elapsed);
  const progress = tabProgress(phase, elapsed);

  useEffect(() => {
    if (process.env.NODE_ENV === "production" || phase !== "enter") return;
    const fresh = deriveStage(elapsed);
    if (fresh.stage !== 0 || fresh.p !== 0 || tenderCounters(fresh.stage, fresh.p).bids !== 0) {
      console.error("hero timeline: incoming slide is not clean", fresh);
    }
  }, [phase, cycleId, elapsed]);

  const tabs = [
    { id: "hero-slide-tenders", label: t("tenders"), icon: ClipboardCheck },
    { id: "hero-slide-contracts", label: t("contracts"), icon: File },
  ] as const;

  const goToSlide = (index: HeroSlide) => {
    if (index === activeSlide) return;
    if (!reduced && phase !== "play") return;
    selectSlide(index);
  };

  return (
    <div
      className="w-full"
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
    >
      <div
        role="tablist"
        aria-label={t("label")}
        className="mx-auto mb-5 flex w-fit rounded-full border-[1.5px] border-brand-navy bg-brand-cream p-1"
        onKeyDown={(event) => {
          if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
          event.preventDefault();
          if (!reduced && phase !== "play") return;
          const next = ((activeSlide + 1) % 2) as HeroSlide;
          goToSlide(next);
          document.getElementById(tabs[next].id)?.focus();
        }}
      >
        {tabs.map((tab, index) => {
          const active = activeSlide === index;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              id={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              aria-pressed={active}
              aria-controls={`${tab.id}-panel`}
              tabIndex={active ? 0 : -1}
              onClick={() => goToSlide(index as HeroSlide)}
              className={cn(
                "relative inline-flex min-h-11 items-center gap-2 rounded-full px-[22px] text-base font-bold transition-colors duration-200",
                active ? "bg-brand-navy text-brand-cream" : "bg-transparent text-brand-navy",
              )}
            >
              <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
              {tab.label}
              {active ? (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute bottom-1.5 left-[22px] right-[22px] h-[3px] origin-left rounded-full bg-brand-accent"
                  style={{ transform: `scaleX(${progress})` }}
                />
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="grid">
        <div
          id="hero-slide-tenders-panel"
          role="tabpanel"
          aria-labelledby="hero-slide-tenders"
          aria-hidden={activeSlide !== 0}
          ref={(node) => {
            if (node) node.inert = activeSlide !== 0;
          }}
          className={cn(
            "hero-slide-pane col-start-1 row-start-1 flex",
            activeSlide === 0 ? "visible" : "pointer-events-none invisible opacity-0",
          )}
          style={slideStyle(0, phase, activeSlide === 0, reduced)}
        >
          <TenderStageCard
            key={`tenders-${cycleId}`}
            stage={activeSlide === 0 ? shown.stage : 0}
            progress={activeSlide === 0 ? shown.p : 0}
            showChips={activeSlide === 0 && phase === "play"}
            motion={activeSlide === 0 && phase === "play"}
            onSelectStage={selectStep}
          />
        </div>
        <div
          id="hero-slide-contracts-panel"
          role="tabpanel"
          aria-labelledby="hero-slide-contracts"
          aria-hidden={activeSlide !== 1}
          ref={(node) => {
            if (node) node.inert = activeSlide !== 1;
          }}
          className={cn(
            "hero-slide-pane col-start-1 row-start-1 flex",
            activeSlide === 1 ? "visible" : "pointer-events-none invisible opacity-0",
          )}
          style={slideStyle(1, phase, activeSlide === 1, reduced)}
        >
          <ContractStageCard
            key={`contracts-${cycleId}`}
            stage={activeSlide === 1 ? shown.stage : 0}
            progress={activeSlide === 1 ? shown.p : 0}
            showChips={activeSlide === 1 && phase === "play"}
            motion={activeSlide === 1 && phase === "play"}
            onSelectStage={selectStep}
          />
        </div>
      </div>
    </div>
  );
}
